/**
 * Units of Measure — manage page.
 *
 * DataTable + Sheet CRUD over the shared AppSettings units. Units are not
 * deleted — archiving sets `isArchived=true` so historical references keep
 * working.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Pencil, Plus, Ruler, Trash2, Undo2 } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import {
  UnitForm,
  type UnitFormValues,
  type UnitDimension,
} from "../../../components/forms/UnitForm";
import { useAppSettings, type SharedUnitRow } from "../../../lib/appSettings";
import { mockProducts, type MockProduct } from "../../../lib/inventoryMock";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/settings/units")({
  component: UnitsPage,
});

type UnitRow = SharedUnitRow;

const DIMENSION_LABEL: Record<string, string> = {
  count: "Count",
  mass: "Mass",
  length: "Length",
  volume: "Volume",
  area: "Area",
};

function UnitsPage() {
  const { units: rows, updateUnits: setRows } = useAppSettings();
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<UnitRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<UnitRow | null>(null);

  const archiveProductCount = useMemo(() => {
    if (!archiveTarget) return 0;
    return products.filter((p) => p.primaryUnitId === archiveTarget._id).length;
  }, [archiveTarget, products]);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: UnitRow) {
    setEditing(row);
    setSheetOpen(true);
  }

  function handleSubmit(values: UnitFormValues) {
    if (editing) {
      setRows((prev) =>
        prev.map((u) =>
          u._id === editing._id
            ? {
                ...u,
                displayName: values.displayName,
                dimension: values.dimension,
                conversionFactor: values.conversionFactor,
                decimalPlaces: values.decimalPlaces,
                isActive: values.isActive,
              }
            : u,
        ),
      );
      toast.success("Unit updated. Audit trail recorded.");
    } else {
      const newId = `unit_${Date.now()}`;
      setRows((prev) => [
        ...prev,
        {
          _id: newId,
          code: values.code,
          displayName: values.displayName,
          dimension: values.dimension,
          conversionFactor: values.conversionFactor,
          isActive: values.isActive,
          decimalPlaces: values.decimalPlaces,
          isArchived: false,
        },
      ]);
      toast.success("Unit created (mock)");
    }
    setSheetOpen(false);
  }

  function handleArchive(row: UnitRow) {
    setArchiveTarget(row);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    setRows((prev) =>
      prev.map((u) =>
        u._id === archiveTarget._id ? { ...u, isActive: false, isArchived: true } : u,
      ),
    );
    toast.success("Unit archived. Existing references kept.");
    setArchiveTarget(null);
  }

  function handleRestore(row: UnitRow) {
    setRows((prev) =>
      prev.map((u) =>
        u._id === row._id ? { ...u, isActive: true, isArchived: false } : u,
      ),
    );
    toast.success("Unit restored");
  }

  const columns = useMemo<DataTableColumn<UnitRow>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Label",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "dimension",
        header: "Dimension",
        cell: (info) => (
          <Badge variant="neutral" size="sm">
            {DIMENSION_LABEL[info.row.original.dimension] ??
              info.row.original.dimension}
          </Badge>
        ),
      },
      {
        id: "decimalPlaces",
        header: "Decimals",
        cell: (info) => (
          <span className="tabular-nums text-(--text-primary)">
            {info.row.original.decimalPlaces}
          </span>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => {
          const r = info.row.original;
          if (r.isArchived) {
            return (
              <Badge variant="neutral" size="sm">
                Archived
              </Badge>
            );
          }
          return (
            <Badge variant={r.isActive ? "success" : "neutral"} size="sm">
              {r.isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(r);
                }}
              >
                <Pencil className="size-3.5" />
              </Button>
              {r.isArchived ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(r);
                  }}
                >
                  <Undo2 className="size-3.5" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchive(r);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Units of Measure"
        description="Quantity units used everywhere stock and pricing surface. Archive instead of deleting — historical movements depend on these codes."
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add unit
          </Button>
        }
      />

      <DataTable
        data={rows}
        columns={columns}
        emptyTitle="No units yet"
        emptyDescription="Create your first unit (pcs, kg, ctn, …)."
        emptyIcon={<Ruler className="size-5 text-(--text-tertiary)" />}
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit unit" : "New unit"}</SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the label or decimals. Code is locked because past records reference it."
                : "Use a short, lowercase code such as pcs, kg, ctn — they show up on every line item."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <UnitForm
              key={editing?._id ?? "new"}
              isEdit={Boolean(editing)}
              initialValues={
                editing
                  ? {
                      code: editing.code,
                      displayName: editing.displayName,
                      dimension: editing.dimension as UnitDimension,
                      decimalPlaces: editing.decimalPlaces,
                      conversionFactor: editing.conversionFactor,
                      isActive: editing.isActive,
                    }
                  : undefined
              }
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-500" />
              Archive unit
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col gap-3 text-sm">
                <p>
                  Archiving{" "}
                  <strong>{archiveTarget?.displayName}</strong>{" "}
                  ({archiveTarget?.code})
                </p>
                {archiveProductCount > 0 && (
                  <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span>
                      {archiveProductCount} product(s) currently use this unit.
                      They will retain their existing unit reference, but this
                      unit won't be available for new products.
                    </span>
                  </p>
                )}
                <p>Existing records that reference this unit are unaffected.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button variant="secondary" onClick={confirmArchive}>
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
